import axios, { type InternalAxiosRequestConfig } from 'axios'

export const TOKEN_KEY = 'immo-web-token'

const api = axios.create({
  baseURL: '/api',
  withCredentials: true,
})

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY)
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean }

let refreshPromise: Promise<string> | null = null

function requestRefresh(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = api
      .post<{ token: string }>('/auth/refresh')
      .then((res) => res.data.token)
      .finally(() => {
        refreshPromise = null
      })
  }
  return refreshPromise
}

function logoutAndRedirect() {
  localStorage.removeItem(TOKEN_KEY)
  if (window.location.pathname !== '/login') {
    window.location.assign('/login')
  }
}

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const config = err.config as RetriableConfig | undefined
    const noRetryEndpoints = ['/auth/refresh', '/auth/login', '/auth/register', '/auth/logout']
    const skipRetry = noRetryEndpoints.some((p) => config?.url?.startsWith(p))

    if (err.response?.status === 401 && config && !config._retry && !skipRetry) {
      config._retry = true
      try {
        const token = await requestRefresh()
        localStorage.setItem(TOKEN_KEY, token)
        config.headers = config.headers ?? {}
        config.headers.Authorization = `Bearer ${token}`
        return api(config)
      } catch {
        logoutAndRedirect()
        return Promise.reject(err)
      }
    }

    if (err.response?.status === 401 && !skipRetry) {
      logoutAndRedirect()
    }
    return Promise.reject(err)
  },
)

export function apiErrorMessage(err: unknown, fallback = 'Une erreur est survenue'): string {
  if (axios.isAxiosError(err)) {
    return err.response?.data?.error || err.message || fallback
  }
  return fallback
}

export default api
